"use client";

/**
 * P11-23 · The geo-disclosure impression/outcome pair.
 *
 * The banner and the picker are separate components on different parts of the
 * page, and the shopper may browse several pages between seeing one and using
 * the other. This module is the seam between them: the banner records what was
 * defaulted, the picker reports whether the shopper overrode it.
 *
 * ## Why these two events and not "accepted"
 *
 * P11-19's banner discloses a market that is ALREADY APPLIED — geo defaults the
 * market, it no longer offers one. There is no accept button, so agreement is
 * silence and the only observable act is disagreement. The pair is therefore
 * impression + override, and impressions-minus-overrides is the agreement rate.
 *
 * ## Why the impression is deduplicated
 *
 * The banner re-renders on every page view until it is dismissed (its dismissal
 * cookie is only written by the close button). Emitting per render would count
 * one shopper browsing ten pages as ten impressions and turn a 100% override
 * rate into 10%. The denominator has to be "a shopper who was told", so the
 * impression is recorded once per arrival country per session.
 *
 * Session, not forever: a shopper returning next week is genuinely being told
 * again and can genuinely disagree again.
 */
import { track } from "~/lib/analytics/client";
import { currentGrants } from "~/lib/analytics/consent";

const KEY = "vx_market_disclosure";

/** What geo chose for this shopper — the mapping an override would be judging. */
export interface Disclosure {
  country: string;
  region: string | null;
  currency: string | null;
}

function read(): Disclosure | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Disclosure) : null;
  } catch {
    return null;
  }
}

function write(value: Disclosure | null): void {
  try {
    if (value) sessionStorage.setItem(KEY, JSON.stringify(value));
    else sessionStorage.removeItem(KEY);
  } catch {
    // Private-mode or storage-disabled browsers simply lose the correlation;
    // the impression still fires and the override is skipped, which under-counts
    // rather than inventing a number.
  }
}

/**
 * Record that the disclosure was shown. Idempotent per country per session.
 *
 * The consent check duplicates `track`'s own gate on purpose: without it a
 * declined visitor would still burn the once-per-session marker, so if they
 * later granted consent their impression could never be recorded.
 * `currentGrants` is the public equivalent of the gate inside `track` — a
 * visitor who has never answered gets nothing, same as there.
 */
export function recordDisclosureImpression(d: Disclosure): void {
  if (typeof window === "undefined") return;
  if (!currentGrants().analytics) return;

  const seen = read();
  if (seen?.country === d.country) return;

  write(d);
  track("MARKET_SUGGESTED", {
    props: { country: d.country, region: d.region, currency: d.currency },
  });
}

/**
 * Report a market the shopper applied themselves. Emits only when it actually
 * differs from what geo defaulted — reopening the picker and re-applying the
 * same market is agreement, not an override.
 *
 * Fires at most once per disclosure: the stored mapping is cleared afterwards,
 * so a shopper who changes their mind twice is one override, not two.
 */
export function reportMarketOverride(applied: {
  currency: string | null;
  region: string | null;
}): void {
  if (typeof window === "undefined") return;

  const disclosed = read();
  if (!disclosed) return;

  const overrodeRegion =
    applied.region !== null && applied.region !== disclosed.region;
  const overrodeCurrency =
    applied.currency !== null && applied.currency !== disclosed.currency;
  if (!overrodeRegion && !overrodeCurrency) return;

  write(null);
  track("MARKET_SUGGESTION_OVERRIDDEN", {
    props: {
      // The arrival country and what we mapped it to — an elevated override
      // rate is only actionable if it names the mapping that is wrong.
      country: disclosed.country,
      defaultedRegion: disclosed.region,
      defaultedCurrency: disclosed.currency,
      overriddenToRegion: overrodeRegion ? applied.region : null,
      overriddenToCurrency: overrodeCurrency ? applied.currency : null,
    },
  });
}
